import { gql, useQuery } from "@apollo/client";
import PageHeader from "../theme/components/common/PageHeader";
import * as React from "react";
import Table from "react-bootstrap/Table";
import Card from "react-bootstrap/Card";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { toast } from "react-toastify";
import debounce from "lodash/debounce";
import apiClient from "../api";
import { gettext } from "../i18n";
import { DataSource } from "utils/datacharts";
import { GetAllStationAlarmsQuery } from "pages/__generated__/GetAllStationAlarmsQuery";
import SessionTimeout from "components/common/SessionTimeout";
import { formatNumberLocale } from "utils/formatting";
import { formatNoDataPeriod, isStationNoDataAlarm } from "utils/alarms";
import ConfigureAlarmsForm from "components/modals/ConfigureAlarmsForm";
import AddNewAlarmForm from "components/modals/AddNewAlarmForm";
import { useModalContext } from "state/modal";
import { useNavigate } from "react-router-dom";
import "./AlarmsPage.scss";

export const GET_ALL_STATION_ALARMS = gql`
  query GetAllStationAlarmsQuery($searchQuery: String) {
    alert(
      where: {
        _or: [
          { station: { name: { _ilike: $searchQuery } } }
          { station: { identifier: { _ilike: $searchQuery } } }
          { station: { organization: { name: { _ilike: $searchQuery } } } }
        ]
      }
    ) {
      id
      kind
      station_id
      no_data_minutes
      triggered_at
      lower_limit
      upper_limit
      alert_frequency_minutes
      channel_id
      formula_id
      active
      level
      source_uuid
      alert_timeframe_minutes
      alert_recipients {
        id
        alert_id
        email_recipient
        sms_recipient
      }
      station {
        id
        identifier
        name
        organization {
          name
        }
      }
      datasource {
        type
        source_id
        name
        order
        visible
        unit {
          id
          symbol
        }
      }
    }
  }
`;

const AlarmsPage = () => {
  const { activeModal, setActiveModal, setSelectedDataSource } =
    useModalContext();
  const [stationQuery, setStationQuery] = React.useState("");
  const [debouncedStationQuery, setDebouncedStationQuery] = React.useState("");
  const debouncedSetQuery = React.useMemo(
    () => debounce((value: string) => setDebouncedStationQuery(value), 400),
    [],
  );

  React.useEffect(() => {
    debouncedSetQuery(stationQuery);
    return () => debouncedSetQuery.cancel();
  }, [stationQuery, debouncedSetQuery]);

  const {
    data,
    error,
    refetch: alarmsRefetch,
  } = useQuery<GetAllStationAlarmsQuery>(GET_ALL_STATION_ALARMS, {
    variables: { searchQuery: `%${debouncedStationQuery}%` },
  });

  const showAlertSetting = React.useMemo(
    () => (source: DataSource) => (e: any) => {
      e.preventDefault();
      setSelectedDataSource(source);
      setActiveModal("configureAlarms");
    },
    [setSelectedDataSource, setActiveModal],
  );

  if (!data) {
    return <div />;
  }

  if (error) {
    console.error(JSON.stringify(error, null, 2));
    return <SessionTimeout />;
  }

  const alerts = data?.alert || [];

  // Sort alerts by station name, then by parameter name
  const sortedAlerts = [...alerts].sort((a, b) => {
    // First, compare station names
    const stationA = a.station?.name || "";
    const stationB = b.station?.name || "";
    const stationComparison = stationA.localeCompare(stationB);

    if (stationComparison !== 0) {
      return stationComparison;
    }

    // If stations are the same, compare parameter names
    const paramA = a.datasource?.name || "";
    const paramB = b.datasource?.name || "";
    return paramA.localeCompare(paramB);
  });

  return (
    <div className="AlarmsPage">
      {activeModal === "configureAlarms" && <ConfigureAlarmsForm />}
      {activeModal === "addNewAlarm" && <AddNewAlarmForm />}
      <PageHeader title={gettext("Alarms")} className="mb-3"></PageHeader>
      <Card className="mb-3">
        <Card.Body>
          <Row className="g-3 mb-3">
            <Col xs={12} lg={6}>
              <Form.Label className="fw-semibold">Station</Form.Label>
              <Form.Control
                type="text"
                placeholder={gettext(
                  "Search by station name, identifier or organization",
                )}
                value={stationQuery}
                onChange={(e) => setStationQuery(e.target.value)}
              />
            </Col>
          </Row>
          <AlarmsTable
            alerts={sortedAlerts}
            showAlertSetting={showAlertSetting}
            onSuccess={() => {
              alarmsRefetch().catch(console.error);
            }}
          />
        </Card.Body>
      </Card>
    </div>
  );
};

// Station not sending data alarms keep their triggered state until the station sends data again
const TriggeredBadge = ({ alert }: { alert: any }) =>
  isStationNoDataAlarm(alert) && alert.triggered_at ? (
    <span
      className="badge bg-warning text-dark ms-2"
      title={gettext("The station is currently not sending data")}
    >
      {gettext("Triggered")}
    </span>
  ) : null;

interface AlarmsTableProps {
  alerts: any[];
  showAlertSetting: (source: DataSource) => (e: any) => void;
  onSuccess?: () => void;
}

const AlarmsTable = ({
  alerts,
  showAlertSetting,
  onSuccess,
}: AlarmsTableProps) => {
  const { setActiveModal, setSelectedDataSource, setModalPayload } =
    useModalContext();
  const navigate = useNavigate();

  const deleteHandler = (alert: any) => (e: any) => {
    e.preventDefault();

    if (
      !window.confirm(gettext("Are you sure you want to delete this alarm?"))
    ) {
      return;
    }

    // Construct resource_key from alert data; station-level alarms are authorized by their station
    const datasource = alert.datasource;
    const resource_key = datasource
      ? `${datasource.type}-${datasource.source_id}`
      : alert.channel_id
        ? `channel-${alert.channel_id}`
        : `formula-${alert.formula_id}`;
    const requestParams = isStationNoDataAlarm(alert)
      ? { stationId: alert.station_id }
      : { resource_key };

    apiClient
      .request("/organization-admin/alert/delete", {
        method: "POST",
        data: {
          ...requestParams,
          alert_id: alert.id,
        },
      })
      .then(() => {
        toast.success(gettext("Alarm deleted successfully"));
        if (onSuccess) {
          onSuccess();
        }
      })
      .catch((e) => {
        console.error(e);
        toast.error(
          `${gettext("Error deleting alarm")}: ${
            e.message?.toString() || "Unknown error"
          }`,
        );
      });
  };

  const handleAddNewAlarm = (e: any) => {
    e.preventDefault();
    setSelectedDataSource(null);
    setModalPayload(null);
    setActiveModal("addNewAlarm");
  };

  const editNoDataAlarmHandler = (alert: any) => (e: any) => {
    e.preventDefault();
    setSelectedDataSource(null);
    setModalPayload(alert);
    setActiveModal("addNewAlarm");
  };

  const editHandler = (alert: any, datasource: DataSource | null) =>
    isStationNoDataAlarm(alert)
      ? editNoDataAlarmHandler(alert)
      : datasource
        ? showAlertSetting(datasource)
        : undefined;

  const canEdit = (alert: any, datasource: DataSource | null) =>
    isStationNoDataAlarm(alert) || !!datasource;

  const formatParameter = (alert: any) =>
    isStationNoDataAlarm(alert)
      ? gettext("Station not sending data")
      : alert.datasource?.name || "-";

  const handleViewAlarmLog = (e: any) => {
    e.preventDefault();
    navigate("/alarm-log");
  };

  const formatAlarmLimits = (alert: any) => {
    if (isStationNoDataAlarm(alert)) {
      return `${gettext("No data for")}: ${formatNoDataPeriod(
        alert.no_data_minutes,
      )}`;
    }

    const limits: string[] = [];

    if (alert.upper_limit !== null && alert.upper_limit !== undefined) {
      limits.push(
        `${gettext("Upper")}: ${formatNumberLocale(alert.upper_limit)}`,
      );
    }

    if (alert.lower_limit !== null && alert.lower_limit !== undefined) {
      limits.push(
        `${gettext("Lower")}: ${formatNumberLocale(alert.lower_limit)}`,
      );
    }

    return limits.length > 0 ? limits.join(", ") : "-";
  };

  const formatRecipients = (alert_recipients: any[]) => {
    if (!alert_recipients || alert_recipients.length === 0) {
      return "-";
    }

    const recipients: string[] = [];

    alert_recipients.forEach((recipient) => {
      if (recipient.email_recipient) {
        recipients.push(recipient.email_recipient);
      }
      if (recipient.sms_recipient) {
        recipients.push(recipient.sms_recipient);
      }
    });

    return recipients.length > 0 ? recipients.join(", ") : "-";
  };

  return (
    <div>
      <div className="mb-3 d-flex flex-wrap gap-2">
        <Button variant="primary" size="sm" onClick={handleAddNewAlarm}>
          Add new alarm
        </Button>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={handleViewAlarmLog}
        >
          Alarm log
        </Button>
      </div>

      <div className="d-none d-md-block overflow-auto">
        <Table style={{ tableLayout: "auto" }}>
          <thead>
            <tr>
              <th style={{ width: "15%" }}>Organization</th>
              <th style={{ width: "20%" }}>Station</th>
              <th style={{ width: "20%" }}>Parameter</th>
              <th style={{ width: "15%" }}>Set alarm</th>
              <th style={{ width: "20%" }}>Recipients</th>
              <th style={{ width: "auto" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {alerts.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center">
                  No alarms configured
                </td>
              </tr>
            ) : (
              alerts.map((alert) => {
                const datasource = alert.datasource
                  ? {
                      ...alert.datasource,
                      alert_id: alert.id,
                      lower_limit: alert.lower_limit,
                      upper_limit: alert.upper_limit,
                      alert_frequency_minutes: alert.alert_frequency_minutes,
                      alert_timeframe_minutes: alert.alert_timeframe_minutes,
                      active: alert.active,
                      level: alert.level,
                      source_uuid: alert.source_uuid,
                      alert_recipients: alert.alert_recipients,
                    }
                  : null;

                return (
                  <tr key={alert.id}>
                    <td className="text-break">
                      {alert.station?.organization?.name ?? "—"}
                    </td>
                    <td className="text-break">{alert.station?.name || "-"}</td>
                    <td className="text-break">{formatParameter(alert)}</td>
                    <td>
                      {formatAlarmLimits(alert)}
                      <TriggeredBadge alert={alert} />
                    </td>
                    <td className="text-break">
                      {formatRecipients(alert.alert_recipients)}
                    </td>
                    <td>
                      <div className="d-flex flex-wrap gap-1">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={editHandler(alert, datasource)}
                          disabled={!canEdit(alert, datasource)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={deleteHandler(alert)}
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>

      <div className="d-md-none">
        {alerts.length === 0 ? (
          <div className="text-center py-3">No alarms configured</div>
        ) : (
          alerts.map((alert) => {
            const datasource = alert.datasource
              ? {
                  ...alert.datasource,
                  alert_id: alert.id,
                  lower_limit: alert.lower_limit,
                  upper_limit: alert.upper_limit,
                  alert_frequency_minutes: alert.alert_frequency_minutes,
                  alert_timeframe_minutes: alert.alert_timeframe_minutes,
                  active: alert.active,
                  level: alert.level,
                  source_uuid: alert.source_uuid,
                  alert_recipients: alert.alert_recipients,
                }
              : null;

            return (
              <Card key={alert.id} className="mb-2">
                <Card.Body className="p-3">
                  <div className="mb-2">
                    <div className="text-muted small">
                      {alert.station?.organization?.name ?? "—"}
                    </div>
                    <div className="fw-semibold">
                      {alert.station?.name || "-"}
                    </div>
                    <div className="small text-muted">
                      {formatParameter(alert)}
                    </div>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="small">
                      {formatAlarmLimits(alert)}
                      <TriggeredBadge alert={alert} />
                    </span>
                  </div>
                  <div className="text-muted small mb-2">
                    {formatRecipients(alert.alert_recipients)}
                  </div>
                  <div className="d-flex flex-wrap gap-1">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={editHandler(alert, datasource)}
                      disabled={!canEdit(alert, datasource)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={deleteHandler(alert)}
                    >
                      Delete
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
};

export default AlarmsPage;
